import React, { useState, useEffect } from 'react';
import { Box, Stack, Icon, Avatar, Typography, useTheme } from '@mui/material';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import { reduxHelper } from '../../../config/reduxHelper';
import TextPropertiesPanel from '../../siteBuilder/properties/TextPropertiesPanel';
import useDevice from '../../../hooks/useDevice';

const FlexibleFeatureCardBlock = ({ block, isSiteBuilder, isActive, handleUpdate, pageFontSx, onDelete, onDuplicate }) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { push } = useRouter();
    const { siteBuilderSelectedChild, pageJson } = useSelector((s) => s.reducer);
    const [isHovered, setIsHovered] = useState(false);
    const { isMobile, isTablet } = useDevice();

    const fontFamily = pageJson?.pageStyle?.fontFamily || 'Roboto';

    useEffect(() => {
        if (isSiteBuilder && isActive && siteBuilderSelectedChild?.uid !== block.uid) {
            dispatch({
                type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                payload: { ...block },
            });
        }
    }, [isActive, isSiteBuilder, block, siteBuilderSelectedChild, dispatch]);

    const handleClick = (e) => {
        if (isSiteBuilder) {
            e.stopPropagation();
            // Ensure selection sync on click
            if (siteBuilderSelectedChild?.uid !== block.uid) {
                dispatch({
                    type: reduxHelper.UPDATE_SITE_BUILDER_SELECTED_CHILD,
                    payload: { ...block },
                });
            }
            return;
        }

        if (block.url) {
            if (typeof block.url === 'string') {
                if (block.url.startsWith('http')) {
                    window.open(block.url, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url);
                }
            } else if (typeof block.url === 'object' && block.url.path) {
                if (block.url.target) {
                    window.open(block.url.path, '_blank', 'noopener,noreferrer');
                } else {
                    push(block.url.path);
                }
            }
        }
    };

    const { content } = block;
    if (!content) return null;
    const { icon } = content;
    const props = block.props || {};

    const borderRadius = (() => {
        switch (props?.borderRadius) {
            case "Full": return "100px";
            case "Medium": return "40px";
            case "Low": return "20px";
            case "None": return "0px";
            default: return "0px";
        }
    })();

    const boxStyles = {
        width: props.fontSize === "large" ? "100px" : props.fontSize === "medium" ? "80px" : props.fontSize === "small" ? "60px" : "80px",
        backgroundColor: props?.style3?.color || theme.palette.primary.main,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: props.fontSize === "large" ? "100px" : props.fontSize === "medium" ? "80px" : props.fontSize === "small" ? "60px" : "80px",
    };

    const shadowStyles = {
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.6)",
        borderRadius: borderRadius,
    };

    const nonShadowStyles = {
        borderRadius: borderRadius,
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                maxHeight: '132px'
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {isSiteBuilder && isActive && (
                <Box
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 10000,
                        mb: 1
                    }}
                >
                    <TextPropertiesPanel
                        childOverride={block}
                        onChangeChild={(updated) => handleUpdate(updated)}
                        onDeleteChild={() => onDelete && onDelete(block.id)}
                        onDuplicateChild={() => onDuplicate && onDuplicate(block.id)}
                    />
                </Box>
            )}

            <Stack
                onClick={handleClick}
                width={"100%"}
                height={"132px"}
                sx={{
                    ...(isHovered ? nonShadowStyles : shadowStyles),
                    bgcolor: props?.style2?.color || 'background.paper',
                    p: "1rem",
                    transition: "transform 0.2s",
                    cursor: isSiteBuilder ? 'default' : (block.url ? 'pointer' : 'default'),
                    ...props?.sx,
                }}
            >
                <Stack direction={"row"} alignItems={"center"} gap={3} height={"100%"}>
                    <Avatar sx={boxStyles}>
                        <Icon
                            className={`mdi mdi-${icon}`}
                            sx={{
                                color: props?.style4?.color || '#fff',
                                fontSize: (t) => ({
                                    large: t.typography.pxToRem(58),
                                    medium: t.typography.pxToRem(48),
                                    small: t.typography.pxToRem(38),
                                }[props.fontSize] || "2.25rem"),
                            }}
                        />
                    </Avatar>

                    <Box sx={{ width: '80%', flex: 1 }}>
                        <Typography
                            contentEditable={isSiteBuilder}
                            suppressContentEditableWarning
                            onBlur={(e) => {
                                const val = e.currentTarget.innerText;
                                handleUpdate({
                                    ...block,
                                    content: { ...block.content, content: val }
                                });
                            }}
                            onClick={(e) => isSiteBuilder && e.stopPropagation()}
                            sx={{
                                color: props?.style5?.textcolor || 'text.primary',
                                fontSize: isMobile ? props?.style1?.fontSize / 1.3 : isTablet ? props?.style1?.fontSize : props?.style1?.fontSize,
                                fontWeight: props?.style1?.fontWeight || 400,
                                fontStyle: props?.style1?.fontStyle || 'normal',
                                fontFamily: `${fontFamily}!important`,
                                outline: 'none',
                                cursor: isSiteBuilder ? 'text' : 'inherit',
                                ...(isSiteBuilder ? {} : {
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    WebkitBoxOrient: "vertical",
                                    WebkitLineClamp: 2,
                                }),
                            }}
                        >
                            {content?.content || (isSiteBuilder ? 'Feature description text...' : '')}
                        </Typography>
                    </Box>
                </Stack>
            </Stack>
        </Box>
    );
};

export default FlexibleFeatureCardBlock;
